import React, { useState, useEffect, useCallback } from 'react';
import { format, subDays, parseISO } from 'date-fns';
import ReportLayout, { ReportTable, usePagination, fmtId } from './ReportLayout';
import { ReportChartRow, BarCard, groupCount, groupByMonth } from './ReportCharts';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useShiftTypes } from '../../hooks/useSettings';
import { fmtTime12 } from '../../utils/time';

const fmtDate = v => { try { return v ? format(parseISO(v), 'd MMM yyyy') : '–'; } catch { return v||'–'; } };
const fmtTS   = v => { try { return v ? format(parseISO(v), 'd MMM yy h:mm a') : '–'; } catch { return v||'–'; } };

const COLS = [
  { key:'shift_id',     label:'Shift ID',     render: fmtId },
  { key:'start_at',     label:'Start At',      render: fmtDate },
  { key:'end_at',       label:'End At',         render: v => v ? fmtTime12(v.split(' ')[1] || v) : '–' },
  { key:'client',       label:'Client' },
  { key:'price_book',   label:'Price Book' },
  { key:'shift_type',   label:'Shift Type' },
  { key:'_team',        label:'Team',           render: () => '–' },
  { key:'facility',     label:'Facility' },
  { key:'address',      label:'Address' },
  { key:'duration_hrs', label:'Duration (Hrs)' },
  { key:'created_at',   label:'Created At',    render: fmtTS },
];

export default function VacantShifts() {
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [startDate, setStart] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEnd]     = useState(new Date(Date.now()+30*86400000).toISOString().split('T')[0]);
  const [shiftType, setShiftType] = useState('');
  const shiftTypes = useShiftTypes();
  const pagination = usePagination(data, 10);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ start_date: startDate, end_date: endDate });
    if (shiftType) p.set('shift_type', shiftType);
    api.get(`/reports/vacant-shifts?${p}`)
      .then(r => { setData(r.data||[]); pagination.reset(); })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [startDate, endDate, shiftType]);

  useEffect(() => { load(); }, [load]);

  const chips = [
    { label:'Date Range', value:`${startDate} - ${endDate}` },
    ...(shiftType ? [{ label:'Shift Type', value: shiftType, onRemove: () => setShiftType('') }] : []),
  ];

  return (
    <ReportLayout title="Vacant Shifts" data={data} filename="vacant_shifts"
      columns={COLS} activeChips={chips} showFiltersPanel={showFilters} onToggleFilters={() => setShowFilters(v=>!v)}
      chart={
        <ReportChartRow>
          <BarCard title="Upcoming vacancies by month" data={groupByMonth(data, 'start_at')} />
          <BarCard title="Vacant by shift type" data={groupCount(data, 'shift_type')} color="#06B6D4" />
        </ReportChartRow>
      }
      filters={
        <div className="flex flex-wrap gap-4">
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Start Date</label>
            <input type="date" value={startDate} onChange={e => setStart(e.target.value)} className="input-field !w-auto text-sm"/></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">End Date</label>
            <input type="date" value={endDate} onChange={e => setEnd(e.target.value)} className="input-field !w-auto text-sm"/></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Shift Type</label>
            <select value={shiftType} onChange={e => setShiftType(e.target.value)} className="input-field !w-auto text-sm">
              <option value="">All Types</option>
              {shiftTypes.filter(t => t.is_active).map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
            </select></div>
          <div className="flex items-end"><button onClick={load} className="btn-primary text-sm px-4 py-2">Apply</button></div>
        </div>
      }>
      <ReportTable columns={COLS} data={data} loading={loading} pagination={pagination}/>
    </ReportLayout>
  );
}
